import * as Logger from "js-logger";
import AnimationManager from "slotworks/graphics/animation/animation-manager";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import ParticleManager from "slotworks/graphics/particles/particle-manager";
import { Services } from "slotworks/services/services";
import { AddWindowFocusChangeListener } from "slotworks/utils/browser-utils";
import Contract from "slotworks/utils/contracts/contract";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";
import Stats = require("stats.js");

class GameLoop {

    public fakeFps: number = 60;
    public speed: number = 1;
    public currentFPS: number;

    private stats: Stats;

    private adjustedTime: number = null;
    private lastTime: number = null;
    private pauseOffset: number = 0;

    private pauseReasons: Map<string, boolean> = new Map<string, boolean>();
    private autoPaused: boolean;

    private forcedFrameDrops: number = 0;
    private frameSkipping: boolean = true;

    private frameRate: number = 60;
    private desiredMSBetweenFrames: number = 1000 / this.frameRate;

    // Keeps track of whether the game window is focused / visible
    private focus: boolean = true;

    public init() {

        AddWindowFocusChangeListener((focus: boolean) => { this.changeFocus(focus); });

        (TweenJS as any).now = () => {
            return this.adjustedTime;
        };

        return new Contract<void>((resolve) => {
            requestAnimationFrame((time?: number) => {
                this.render(time);

                resolve(null);
            });
        });
    }

    public showStats() {
        if (!this.stats) {
            this.stats = new Stats();
            this.stats.showPanel(0);
        }
        document.body.appendChild(this.stats.dom);
    }

    public toggleStats() {
        if (this.stats) {
            if (this.stats.dom.parentElement) {
                document.body.removeChild(this.stats.dom);
            } else {
                document.body.appendChild(this.stats.dom);
            }
        }
    }

    /**
     * Sets a manual pause on the game. If called without arguments, pause will toggle
     *
     * @param paused {boolean}
     */
    public setPaused(id: string, paused: boolean = null) {
        if (paused === null) {
            if (this.pauseReasons.has(id)) {
                this.pauseReasons.set(id, !this.pauseReasons.get(id));
            } else {
                this.pauseReasons.set(id, true);
            }
        } else {
            this.pauseReasons.set(id, paused);
        }

        const isPaused = this.getPaused();

        if (!isPaused) {
            this.autoPaused = true;
        }

        this.changeFocus(this.focus);

        this.log(isPaused ? "paused" : "unpaused");
    }

    public setSpeed(speed: number) {
        this.speed = speed;
        this.desiredMSBetweenFrames = 1000 / this.frameRate * this.speed;
    }

    private getPaused(): boolean {
        const values = this.pauseReasons.values();
        let current = values.next();

        while (!current.done) {
            if (current.value) {
                return true;
            }
            current = values.next();
        }
        return false;
    }

    private changeFocus(focus: boolean) {
        const paused = this.getPaused();
        this.focus = focus;
        if (!focus || paused) {
            this.autoPaused = true;
            Services.sound.unfocus();
        } else if (!paused) {
            Services.sound.focus();
        }
    }

    private render(time?: number) {

        if (time) {
            time = time * this.speed;

            if (!this.frameSkipping) {
                if (time - this.lastTime > this.desiredMSBetweenFrames) {
                    time = this.lastTime + this.desiredMSBetweenFrames;
                }
            }
        }

        this.forcedFrameDrops++;

        if (!this.fakeFps || this.forcedFrameDrops >= (this.frameRate / this.fakeFps)) {
            this.forcedFrameDrops = 0;

            if (this.stats) {
                this.stats.begin();
            }

            if (this.adjustedTime === null) {
                this.adjustedTime = time;
            }

            const paused = this.getPaused();

            if (this.autoPaused || paused) {

                if (this.lastTime === null) {
                    this.lastTime = time;
                }
                this.pauseOffset += time - this.lastTime;
                this.autoPaused = false;

            }

            if (!this.autoPaused && !paused) {
                this.adjustedTime = time - this.pauseOffset;
                // if (this.lastTime - this.adjustedTime)

                AnimationManager.update(this.adjustedTime);
                TweenJS.update();
                Timer.update(this.adjustedTime);
                ParticleManager.update(this.adjustedTime);
            }

            CanvasManager.renderer.render(CanvasManager.stage);

            this.currentFPS = (1000 / (time - this.lastTime));
            this.lastTime = time;

            if (this.stats) {
                this.stats.end();
            }
        }

        requestAnimationFrame((t: number) => this.render(t));
    }

    /**
     * Log a message with colored label
     *
     * @param message {string}
     */
    private log(message: string) {
        Logger.info("%c Game Loop ", "background: #f77bed; color: #fff", message);
    }
}

export default new GameLoop();
